// Plays a ready 3D flyover video full screen. Builds a black full-viewport overlay with a looping
// <video> and a close button, and also requests true browser fullscreen where available (the call
// site is a user gesture, so it's allowed).
//
// Lives here rather than in entrypoints/recordings.ts because two surfaces open the same player: the
// recording overview's gallery thumbnail, and the flyover tile that leads a comment's media strip
// (CommentFlyoverThumb.vue), which renders on hike pages too.
//
// A caller that passes a share context also gets a Share button beside close. It opens the existing
// share wizard on its ready step for this very video (see apps/flyover_share/readyVideoShare.ts). The
// overlay closes first: it sits above the wizard's modal, which would otherwise open hidden behind
// it. No context, or a page without the wizard mounted → no button, exactly as before.
import { i18n } from '../i18n';
import {
  buildReadyVideoShareConfig,
  flyoverShareOpener,
  type ReadyVideoShareContext,
} from '../apps/flyover_share/readyVideoShare';

export function openFlyoverVideoFullscreen(videoUrl: string, share?: ReadyVideoShareContext | null): void {
  const overlay = document.createElement('div');
  overlay.className = 'flyover-fullscreen-overlay';

  const video = document.createElement('video');
  video.className = 'flyover-fullscreen-overlay__video';
  video.src = videoUrl;
  video.controls = true;
  video.autoplay = true;
  video.loop = true;
  video.playsInline = true;

  const close = document.createElement('button');
  close.className = 'flyover-fullscreen-overlay__close material-icons';
  close.setAttribute('aria-label', i18n.global.t('utils.open_flyover_video_fullscreen.close_video'));
  close.textContent = 'close';

  const dismiss = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(); };

  close.addEventListener('click', dismiss);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); });
  document.addEventListener('keydown', onKey);

  overlay.appendChild(video);
  overlay.appendChild(close);

  const openShare = share ? flyoverShareOpener() : null;
  if (share && openShare) {
    const shareButton = document.createElement('button');
    shareButton.className = 'flyover-fullscreen-overlay__share material-icons';
    shareButton.setAttribute('aria-label', i18n.global.t('shared.share'));
    shareButton.textContent = 'share';
    shareButton.addEventListener('click', () => {
      // Read before dismissing: the decoded size is the format fallback when the surface has no aspect.
      const cfg = buildReadyVideoShareConfig(videoUrl, share, {
        width: video.videoWidth,
        height: video.videoHeight,
      });
      video.pause();
      dismiss();
      openShare(cfg);
    });
    overlay.appendChild(shareButton);
  }
  document.body.appendChild(overlay);

  overlay.requestFullscreen?.().catch(() => {});
  video.play().catch(() => {});
}

export default openFlyoverVideoFullscreen;
